import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Lists remember their filters while you look at an item (Roadmap 1851).
 *
 * The list saves its filter state (and the scroll position of the main view)
 * in sessionStorage, keyed by the list's address. When the list is shown again
 * with the browser's Back / Forward button (or the app's own "back", which is
 * the same history step), the saved state is given back. Opening the list
 * from the menu or a link starts clean, as the owner asked: "if I go somewhere
 * else, it doesn't have to remember".
 */

const PREFIX = "proc_list_memory:";
/** Scrollable main area of the signed-in app (MainViewPages). */
export const MAIN_VIEW_ID = "proc-main-view";

export const listMemoryKey = (pathname: string, scope = "") =>
  `${PREFIX}${scope ? `${scope}:` : ""}${pathname.replace(/\/+$/, "") || "/"}`;

type Saved<T> = { state?: T; scroll?: number };

const readRecord = <T>(key: string): Saved<T> | undefined => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : undefined;
  } catch {
    return undefined;
  }
};

const writeRecord = <T>(key: string, record: Saved<T>) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(record));
  } catch {
    // Storage full or blocked: the list simply won't be remembered.
  }
};

export const readListMemory = <T>(key: string): T | undefined =>
  readRecord<T>(key)?.state;

export const writeListMemory = <T>(key: string, state: T) =>
  writeRecord(key, { ...readRecord<T>(key), state });

export const readListScroll = (key: string): number | undefined => {
  const scroll = readRecord(key)?.scroll;
  return typeof scroll === "number" && scroll > 0 ? scroll : undefined;
};

export const writeListScroll = (key: string, scroll: number) =>
  writeRecord(key, { ...readRecord(key), scroll: Math.max(0, scroll) });

export const clearListMemory = (key: string) => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
};

export type ListMemory<T> = {
  key: string;
  /** Saved state when the list was reached with Back / Forward, else undefined. */
  restored: T | undefined;
  scroll: boolean;
  /** Saved scroll position still to be put back (Back / Forward only). */
  pendingScroll: { current: number | undefined };
};

/**
 * Step 1, at the top of the list component: what to start from.
 *
 * `restored` is the saved state when the page was reached with Back /
 * Forward ("POP"); a visit from the menu or a link ("PUSH"/"REPLACE") forgets
 * the old state and gets undefined. Read it in the useState initialisers.
 * `scope` keeps lists of different firms apart; `scroll: false` for a second
 * memory on the same page (e.g. labels) that shouldn't touch the scroll.
 */
export const useListMemory = <T>(
  options: { scope?: string; scroll?: boolean } = {},
): ListMemory<T> => {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const { scope = "", scroll = true } = options;

  // Fixed for the life of the list (tabs are separate mounts).
  const [key] = useState(() => listMemoryKey(pathname, scope));
  const [restored] = useState<T | undefined>(() => {
    if (navigationType !== "POP") {
      clearListMemory(key);
      return undefined;
    }
    return readListMemory<T>(key);
  });
  const pendingScroll = useRef<number | undefined>(
    scroll && navigationType === "POP" ? readListScroll(key) : undefined,
  );

  return { key, restored, scroll, pendingScroll };
};

/**
 * Step 2, once the state exists: saves it on every change, saves the main
 * view's scroll position, and puts the saved position back once `ready`
 * (the rows are on screen).
 */
export const useRememberList = <T>(
  memory: ListMemory<T>,
  state: T,
  ready = true,
) => {
  const { key, scroll, pendingScroll } = memory;

  useEffect(() => {
    writeListMemory(key, state);
  }, [key, state]);

  // A layout effect, so the listener is gone the moment the list leaves:
  // the next page's shorter height would otherwise save a scroll of 0.
  useLayoutEffect(() => {
    const view = document.getElementById(MAIN_VIEW_ID);
    if (!view || !scroll) return undefined;
    const onScroll = () => {
      // Until the saved position is back, scroll events come from the
      // previous page's height, not from the person.
      if (pendingScroll.current !== undefined) return;
      writeListScroll(key, view.scrollTop);
    };
    view.addEventListener("scroll", onScroll, { passive: true });
    return () => view.removeEventListener("scroll", onScroll);
  }, [key, scroll, pendingScroll]);

  useEffect(() => {
    if (!ready || pendingScroll.current === undefined) return undefined;
    const target = pendingScroll.current;
    const frame = requestAnimationFrame(() => {
      const view = document.getElementById(MAIN_VIEW_ID);
      if (view) view.scrollTop = target;
      pendingScroll.current = undefined;
    });
    return () => cancelAnimationFrame(frame);
  }, [ready, pendingScroll]);
};
